import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import {
  MapPin,
  Calendar,
  Clock,
  Building,
  ChevronDown,
  ChevronUp,
  Info,
  ThumbsUp,
  ThumbsDown,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RenderHtml } from "@/components/RenderHtml";
import { WageFrequency } from "@api/shared-runtime/enums";
import { formatLocation } from "../utils/format-location";
import { htmlToText } from "../utils/html-to-text";
import { abbreviateProfessionalType } from "../utils/abbreviate-professional-type";
import { formatFacilityWithType } from "../utils/format-facility-with-type";
import { formatSalary } from "../utils/format-salary";
import { ExternalJobBadge } from "../components/external-job-badge";
import { EligibilityBadge } from "../components/eligibility-badge";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import type { RequirementEligibility } from "@api/services/job-post/pure-requirements-evaluator";
import { JobPostProviderActions } from "./job-post-provider/job-post-provider-actions";

type JobPostData = {
  id?: string;
  publishedAt?: string | null;
  title?: string;
  remote?: boolean | null;
  facility?: {
    id: string;
    name: string;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
  } | null;
  facilityType?: {
    id: string;
    name: string;
  } | null;
  practiceSetting?: {
    displayText: string;
  } | null;
  city?: string | null;
  stateCode?: string | null;
  healthcareProfessionalTypes?: Array<{
    id: string;
    name: string;
    code?: string | null;
    aliases?: string[] | null;
  }>;
  wageMinAmount?: string | null;
  wageMaxAmount?: string | null;
  wageFrequencyId?: WageFrequency | null;
  guaranteedBasePay?: boolean | null;
  guaranteedBasePayDescription?: string | null;
  specialties?: Array<{
    id: string;
    name: string;
  }>;
  /** The job form preview's name for the description. */
  jobDescription?: string | null;
  /** The API's name for the description. */
  description?: string | null;
  responsibilities?: string[] | null;
  requiredQualifications?: string[] | null;
  preferredQualifications?: string[] | null;
  durationIds?: number[] | null;
  startDateType?: "immediate" | "specific";
  startDate?: string | null;
  endDateType?: "none" | "specific";
  endDate?: string | null;
  scheduleDescription?: string | null;
  organizationNotes?: string | null;
  externalUrl?: string | null;
  eligibility?: RequirementEligibility;
  decision?: { interested: boolean } | null;
};

export type JobPostProviderProps = {
  jobData: JobPostData;
  onInterestChange?: (params: {
    jobId: string;
    isInterested: boolean | null;
  }) => Promise<void>;
  /**
   * Horizontal swipe-to-decide gesture. On by default for the single-card
   * flow; turn off when the card sits in a scrolling list, where a sideways
   * drag during a vertical scroll could record a decision by accident.
   */
  enableSwipe?: boolean;
  /** Heading level for the title; lists use "h2" under their own page heading. */
  titleAs?: "h1" | "h2";
  /**
   * Zero-impact marker rendered at the bottom of the text area (the end of Key
   * Details, just above the action buttons). Lets a list observe when the
   * reader has reached the end of the text without adding layout.
   */
  textEndMarker?: React.ReactNode;
  /** Optional content rendered under the title and specialties (e.g. the match-strength badge). */
  headerAccessory?: React.ReactNode;
  /** Merged onto the card's outer element, e.g. to drop its border inside a banner. */
  className?: string;
};

export function JobPostProvider({
  jobData,
  onInterestChange,
  enableSwipe = true,
  titleAs: TitleTag = "h1",
  textEndMarker,
  headerAccessory,
  className,
}: JobPostProviderProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [swipeState, setSwipeState] = useState<{
    isDragging: boolean;
    startX: number;
    currentX: number;
    direction: "left" | "right" | null;
  }>({
    isDragging: false,
    startX: 0,
    currentX: 0,
    direction: null,
  });

  if (!jobData) {
    return null;
  }

  const formatDateRange = (): string => {
    // Check if start date type is immediate
    if (jobData.startDateType === "immediate") {
      const endDate = jobData.endDate
        ? format(new Date(jobData.endDate), "MMM d, yyyy")
        : "";
      return endDate ? `Immediate - ${endDate}` : "Immediate start";
    }

    const startDate = jobData.startDate
      ? format(parseISO(jobData.startDate), "MMM d")
      : "";
    const endDate = jobData.endDate
      ? format(parseISO(jobData.endDate), "MMM d, yyyy")
      : "";

    if (startDate && endDate) {
      return `${startDate} - ${endDate}`;
    } else if (startDate) {
      return `Starting ${startDate}`;
    }

    return "Immediate start";
  };

  const getLocationTypeDisplay = (): string | null => {
    if (jobData.remote === true) {
      return "Remote";
    }
    if (jobData.remote === false) {
      return "On-site";
    }
    // If remote is null/undefined, we can infer it's on-site if there's location data
    if (jobData.facility || jobData.city || jobData.stateCode) {
      return "On-site";
    }
    return null;
  };

  const handleInterestChange = async (isInterested: boolean | null) => {
    if (!jobData.id) {
      console.log("Job ID is required");
      return;
    }
    await onInterestChange?.({
      jobId: jobData.id,
      isInterested,
    });
  };

  // Touch event handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enableSwipe) {
      return;
    }
    const touch = e.touches[0];
    setSwipeState({
      isDragging: true,
      startX: touch.clientX,
      currentX: touch.clientX,
      direction: null,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeState.isDragging) {
      return;
    }

    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeState.startX;
    const direction = deltaX > 0 ? "right" : "left";

    setSwipeState((prev) => ({
      ...prev,
      currentX: touch.clientX,
      direction: Math.abs(deltaX) > 20 ? direction : null,
    }));
  };

  const handleTouchEnd = () => {
    if (!swipeState.isDragging) {
      return;
    }

    const deltaX = swipeState.currentX - swipeState.startX;
    const minSwipeDistance = 100; // Minimum distance to trigger action

    if (Math.abs(deltaX) >= minSwipeDistance) {
      if (deltaX > 0) {
        // Swipe right - like
        handleInterestChange(true);
      } else {
        // Swipe left - reject
        handleInterestChange(false);
      }
    }

    setSwipeState({
      isDragging: false,
      startX: 0,
      currentX: 0,
      direction: null,
    });
  };

  // Calculate transform for visual feedback during swipe
  const getSwipeTransform = () => {
    if (!swipeState.isDragging) {
      return "translateX(0px)";
    }

    const deltaX = swipeState.currentX - swipeState.startX;
    const maxTransform = 50; // Maximum pixels to move
    const constrainedDelta = Math.max(
      -maxTransform,
      Math.min(maxTransform, deltaX * 0.3),
    );

    return `translateX(${constrainedDelta}px)`;
  };

  // Get overlay opacity for visual feedback
  const getOverlayOpacity = () => {
    if (!swipeState.isDragging || !swipeState.direction) {
      return 0;
    }

    const deltaX = Math.abs(swipeState.currentX - swipeState.startX);
    return Math.min(0.3, deltaX / 200); // Max opacity of 0.3
  };

  const descriptionHtml = jobData.jobDescription ?? jobData.description;

  const hasExpandableContent = (): boolean => {
    return !!(
      (jobData.responsibilities && jobData.responsibilities.length > 0) ||
      (jobData.requiredQualifications &&
        jobData.requiredQualifications.length > 0) ||
      (jobData.preferredQualifications &&
        jobData.preferredQualifications.length > 0) ||
      descriptionHtml ||
      jobData.scheduleDescription
    );
  };

  const scheduleText = jobData.scheduleDescription
    ? htmlToText(jobData.scheduleDescription)
    : "";

  const location = formatLocation(jobData);
  const facilityWithType = formatFacilityWithType({
    facility: jobData.facility,
    practiceSetting: jobData.practiceSetting,
    facilityType: jobData.facilityType,
  });
  // The location-type badge already says "Remote"; drop rows that repeat it —
  // the bare "Remote" location and a facility line that is just a
  // remote-flavored setting (e.g. "Remote/Virtual").
  const locationRepeatsRemote = location === "Remote";
  const facilityRepeatsRemote =
    !!jobData.remote &&
    !jobData.facility &&
    /remote|virtual/i.test(facilityWithType);

  const isGuaranteedBaseHourly =
    jobData.guaranteedBasePay &&
    jobData.wageFrequencyId === WageFrequency.Hourly;

  const specialtyNames = jobData.specialties?.map((s) => s.name).join(", ");
  const professionalTypeAbbrevs = jobData.healthcareProfessionalTypes
    ?.map((type) => abbreviateProfessionalType(type))
    .join("/");

  const durationBadgeIds = jobData.durationIds ?? [];

  return (
    <div
      className={cn(
        "max-w-xl mx-auto bg-white rounded-2xl overflow-hidden relative touch-pan-y border border-slate-200 shadow-sm",
        className,
      )}
      style={{
        transform: getSwipeTransform(),
        transition: swipeState.isDragging ? "none" : "transform 0.3s ease-out",
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe overlay indicators */}
      {swipeState.direction === "right" && (
        <div
          className="absolute inset-0 bg-green-500 flex items-center justify-start pl-8 z-10 rounded-2xl"
          style={{ opacity: getOverlayOpacity() }}
        >
          <ThumbsUp className="h-12 w-12 text-white" />
        </div>
      )}
      {swipeState.direction === "left" && (
        <div
          className="absolute inset-0 bg-red-500 flex items-center justify-end pr-8 z-10 rounded-2xl"
          style={{ opacity: getOverlayOpacity() }}
        >
          <ThumbsDown className="h-12 w-12 text-white" />
        </div>
      )}

      {/* Main Content */}
      <div className="p-5 sm:p-6 space-y-4">
        {/* The title, its specialties and any accessory read as one header.
            Flex gap (unlike space-y) skips the accessory wrapper when
            empty:hidden removes it, e.g. a job with no match rating. */}
        <div className="flex flex-col gap-1">
          <TitleTag className="text-xl font-bold text-slate-900 leading-snug">
            {jobData.title || "Position details unavailable"}
          </TitleTag>

          {(specialtyNames || professionalTypeAbbrevs) && (
            <p className="text-sm text-slate-600">
              {specialtyNames && (
                <span className="font-medium text-slate-800">
                  {specialtyNames}
                </span>
              )}
              {specialtyNames && professionalTypeAbbrevs && (
                <span className="text-slate-400"> · </span>
              )}
              {professionalTypeAbbrevs}
            </p>
          )}

          {headerAccessory && (
            <div className="mt-1.5 empty:hidden">{headerAccessory}</div>
          )}
        </div>

        {(jobData.wageMinAmount || jobData.wageMaxAmount) && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center text-lg font-semibold text-primary">
              <DollarSign className="h-5 w-5" aria-hidden="true" />
              {formatSalary(jobData)}
            </span>
            {isGuaranteedBaseHourly &&
              (jobData.guaranteedBasePayDescription ? (
                <Popover>
                  <PopoverTrigger>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                      <Info className="h-3 w-3" />
                      Guaranteed base
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4">
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">
                        Guaranteed Base Details
                      </h4>
                      <p className="text-sm text-slate-700">
                        {jobData.guaranteedBasePayDescription}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="inline-flex items-center text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  Guaranteed base
                </span>
              ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {durationBadgeIds.map((durationId) => (
            <span
              key={durationId}
              className="bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium rounded-md px-2 py-0.5"
            >
              {getJobDurationLabel(durationId)}
            </span>
          ))}
          {getLocationTypeDisplay() && (
            <span className="bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium rounded-md px-2 py-0.5">
              {getLocationTypeDisplay()}
            </span>
          )}
          {jobData.externalUrl && <ExternalJobBadge />}
          {jobData.id && <EligibilityBadge eligibility={jobData.eligibility} />}
        </div>

        {/* Key Details */}
        <div className="relative space-y-2.5">
          {location && !locationRepeatsRemote && (
            <div className="flex items-center gap-3">
              <MapPin
                className="h-4 w-4 text-slate-400"
                aria-label="Location"
              />
              <span className="text-sm text-slate-600">{location}</span>
            </div>
          )}
          {facilityWithType && !facilityRepeatsRemote && (
            <div className="flex items-center gap-3">
              <Building
                className="h-4 w-4 text-slate-400"
                aria-label="Facility Information"
              />
              <span className="text-sm text-slate-600">{facilityWithType}</span>
            </div>
          )}

          {scheduleText && (
            <div className="flex items-start gap-3">
              <Clock
                className="h-4 w-4 text-slate-400 shrink-0 mt-0.5"
                aria-label="Work Schedule"
              />
              <span className="text-sm text-slate-600 line-clamp-3">
                {scheduleText}
              </span>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar
              className="h-4 w-4 text-slate-400"
              aria-label="Employment Dates"
            />
            <span className="text-sm text-slate-600">{formatDateRange()}</span>
          </div>

          {/* Absolutely positioned inside Key Details so it takes no space
              (a sibling would add a space-y-4 gap). */}
          {textEndMarker && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            >
              {textEndMarker}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <JobPostProviderActions
          job={jobData}
          facilityWithType={facilityWithType}
          location={location}
          onDecide={handleInterestChange}
        />

        {hasExpandableContent() && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Hide details" : "View details"}
            {isExpanded ? (
              <ChevronUp
                className="h-4 w-4 ml-1"
                aria-label="Collapse details"
              />
            ) : (
              <ChevronDown
                className="h-4 w-4 ml-1"
                aria-label="Expand details"
              />
            )}
          </Button>
        )}
      </div>

      {/* Expandable Details Section */}
      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-5 space-y-5">
          {jobData.scheduleDescription && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Schedule
              </h3>
              <RenderHtml html={jobData.scheduleDescription} />
            </div>
          )}

          {jobData.responsibilities && jobData.responsibilities.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Responsibilities
              </h3>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                {jobData.responsibilities.map((item: string, index: number) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {jobData.requiredQualifications &&
            jobData.requiredQualifications.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">
                  Required Qualifications
                </h3>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                  {jobData.requiredQualifications.map(
                    (item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ),
                  )}
                </ul>
              </div>
            )}

          {jobData.preferredQualifications &&
            jobData.preferredQualifications.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">
                  Preferred Qualifications
                </h3>
                <ul className="list-disc pl-5 space-y-1.5 text-sm text-slate-700">
                  {jobData.preferredQualifications.map(
                    (item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ),
                  )}
                </ul>
              </div>
            )}

          {descriptionHtml && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">
                Job Description
              </h3>
              <RenderHtml
                html={descriptionHtml}
                className="prose prose-sm max-w-none text-slate-700"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
